import { Button } from '../../ui/button';
import { Input } from '../../forms/input';
import { Textarea } from '../../forms/textarea';
import { Label } from '@folio/components';
import { SendIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import type { SubmitFeedbackBody } from 'korint-sdk';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

interface ExtractionFeedbackFormProps {
  feedbackType: 'FIELD_CORRECTION' | 'DOCUMENT_REJECTION';
  fieldKey?: string;
  originalValue?: string;
  onSubmit: (feedback: SubmitFeedbackBody) => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function ExtractionFeedbackForm({
  feedbackType,
  fieldKey,
  originalValue,
  onSubmit,
  onCancel,
  isPending,
}: ExtractionFeedbackFormProps) {
  const { t } = useTranslation();
  const [correctedValue, setCorrectedValue] = useState('');
  const [comment, setComment] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const feedback: SubmitFeedbackBody = {
      feedbackType,
      ...(fieldKey !== undefined && { fieldKey }),
      ...(originalValue !== undefined && { originalValue }),
      ...(feedbackType === 'FIELD_CORRECTION' && correctedValue && { correctedValue }),
      ...(comment && { comment }),
    };
    onSubmit(feedback);
  };

  if (feedbackType === 'DOCUMENT_REJECTION') {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Text size="sm" tone="muted">
          {t('documentReview.feedbackNote')}
        </Text>
        <div>
          <Label htmlFor="extraction-feedback-rejection-comment" className="text-xs">
            {t('documents.extraction.feedback.comment')}
          </Label>
          <Textarea
            id="extraction-feedback-rejection-comment"
            value={comment}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
            placeholder={t('documents.extraction.feedback.commentPlaceholder')}
            className="mt-1 text-sm"
            rows={3}
          />
        </div>
        <Group justify="end" gap={2}>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
            <XIcon className="w-3 h-3 mr-1" />
            {t('documents.extraction.feedback.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={isPending}>
            <SendIcon className="w-3 h-3 mr-1" />
            {t('documentReview.sendReport')}
          </Button>
        </Group>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <Text size="sm" tone="muted">
        {t('documentReview.feedbackNote')}
      </Text>
      <div>
        <Label className="text-xs">{t('documents.extraction.feedback.fieldLabel')}</Label>
        <Text size="sm" weight="medium" className="mt-0.5">
          {t(`documents.extraction.fields.${fieldKey}`, fieldKey ?? '')}
        </Text>
      </div>
      <div>
        <Label className="text-xs">{t('documents.extraction.feedback.originalValue')}</Label>
        <Text size="sm" tone="muted" className="mt-0.5">
          {originalValue ?? '-'}
        </Text>
      </div>
      <div>
        <Label htmlFor="extraction-feedback-corrected-value" className="text-xs">
          {t('documentReview.expectedValue')}
        </Label>
        <Input
          id="extraction-feedback-corrected-value"
          value={correctedValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setCorrectedValue(e.target.value)}
          className="mt-1 text-sm"
        />
      </div>
      <div>
        <Label htmlFor="extraction-feedback-comment" className="text-xs">
          {t('documents.extraction.feedback.comment')}
        </Label>
        <Textarea
          id="extraction-feedback-comment"
          value={comment}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
          placeholder={t('documents.extraction.feedback.commentPlaceholder')}
          className="mt-1 text-sm"
          rows={2}
        />
      </div>
      <Group justify="end" gap={2}>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
          <XIcon className="w-3 h-3 mr-1" />
          {t('documents.extraction.feedback.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={isPending || !correctedValue}>
          <SendIcon className="w-3 h-3 mr-1" />
          {t('documentReview.sendReport')}
        </Button>
      </Group>
    </form>
  );
}
